// Sidebar component with step navigation

import { useEffect, useRef } from 'react';
import { useCurrentStep, useStepLabels } from '@/hooks';
import clsx from 'clsx';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const sidebarRef = useRef<HTMLElement>(null);
  const currentStep = useCurrentStep();
  const stepLabels = useStepLabels();

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        isOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose])

  return (
    <aside
      ref={sidebarRef}
      className={clsx('sidebar', { 'sidebar--open': isOpen })}
    >
      <img
        src="/img/2TIO_logo.png"
        alt="2TurnItOn"
        className="sidebar__logo"
      />

      <nav className="step-nav">
        {stepLabels.map((label, index) => (
          <div
            key={index}
            className={clsx('step-nav__item', {
              'step-nav__item--active': currentStep === index + 1,
              'step-nav__item--completed': currentStep > index + 1,
            })}
          >
            <span className="step-nav__dot"></span>
            <span className="step-nav__label">{label}</span>
          </div>
        ))}
      </nav>

      <div className="sidebar__footer desktop-hide">
        <div className="sidebar__encryption">
          <svg className="sidebar__lock" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="#16a34a">
            <path d="M144 144v48H304V144c0-44.2-35.8-80-80-80s-80 35.8-80 80zM80 192V144C80 64.5 144.5 0 224 0s144 64.5 144 144v48h16c35.3 0 64 28.7 64 64V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V256c0-35.3 28.7-64 64-64H80z"/>
          </svg>
          256-bit encryption
        </div>
        <div className="sidebar__links">
          <a href="https://www.2turniton.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy</a>
          <a href="https://www.2turniton.com/terms-conditions" target="_blank" rel="noopener noreferrer">Terms</a>
        </div>
      </div>
    </aside>
  )
}
